// routes/OverviewPane.jsx — Space 개요 (ux §3.2).
//
// owner 는 `space_memberships` 에 행이 없다(스키마가 그렇다). Gateway 가 `space.ownerNodeId`
// 로부터 정규화 행을 만들어 주지만 그 행에는 `state` 가 없다 — 그래서 화면이 두 모양을 함께
// 다뤄야 한다. 이 사실을 모르면 방금 만든 Space 가 "참여자 없음"으로 보인다.
//
// 참여 신청의 승인·거절이 여기 있다(구현 6단계의 앞부분, 2026-08-11).
// - 승인은 멤버십을 만드는 경계 쓰기라 `expectedAuthorityRevision` 으로 경합을 막는다.
//   그 값(`viewerRevision`)이 없으면 버튼을 그리지 않고 없다고 적는다 — 낙관하지 않는다.
// - 거절은 근거(reason)를 반드시 남긴다. 반대 의견도 공동 기억이라는 원칙이 여기에도 선다.
//   경합 방어는 전역 개정이 아니라 그 요청의 `requestVersion` 이다(join_reject 어휘 주석).

import { useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { command, read } from '../api/client.js';
import { useCommand, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import { Button, ButtonLink, Chip, Field, Form, Icon, List, ListRow, Notice, Textarea } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import './task-reentry.css';
import { SpaceContextSummary } from './SpaceContextPane.jsx';

export function OverviewPane({ spaceId, home }) {
  const session = useSession();
  const space = home.space;
  const members = home.members ?? [];
  const joinRequests = home.joinRequests;
  const archived = space?.state === 'archived';
  // Gateway 는 claim 범위의 authorityRevision 을 viewerRevision 으로 준다. 없으면 space 의 값.
  const revision = home.viewerRevision ?? space?.authorityRevision ?? null;
  // Space는 정보 카드가 아니라 다시 일을 시작하는 경계다. 이 세 읽기는 본문을 복제하지
  // 않고, 각각 원문 대화·Inbox 포인터·Task 목록의 실제 진입점만 읽는다.
  const threads = useResource(`space:${spaceId}:overview:threads`, () =>
    read('conversation-list', { nodeId: session.nodeId, query: { spaceId, limit: 3 } }),
  );
  const inbox = useResource(`space:${spaceId}:overview:inbox`, () =>
    read('inbox-page', { nodeId: session.nodeId, query: { spaceId, limit: 20 } }),
  );
  const tasks = useResource(`space:${spaceId}:overview:tasks`, () =>
    read('task-board', { nodeId: session.nodeId, query: { spaceId, limit: 3 } }),
  );
  const latestThread = threads.status === 'ready' ? (threads.data.threads ?? [])[0] : null;
  const visibleMembers = members.slice(0, 4);
  const leadMember = members.find((member) => member.role === 'owner') ?? members[0];

  return (
    <section class="work work--dashboard">
      <header class="work__head linear-dashboard__head">
        <div class="work__head-text">
          <p class="linear-dashboard__crumb t-meta">SPACE / OVERVIEW</p>
          <h1 class="linear-dashboard__title">{space?.name ?? '공간'}</h1>
          <p class="linear-dashboard__lede t-meta">
            {space?.state === 'archived'
              ? '보관된 공간입니다. 기록은 읽을 수 있고 새 기록은 남길 수 없습니다.'
              : '대화, 판단, Task의 현재 흐름'}
          </p>
        </div>
        {latestThread ? (
          <ButtonLink size="sm" tone="human" href={`/s/${spaceId}/t/${latestThread.threadId}`}>대화 이어가기</ButtonLink>
        ) : !archived ? (
          <ButtonLink size="sm" tone="human" href={`/s/${spaceId}/t/new`}>새 대화</ButtonLink>
        ) : null}
      </header>

      <div class="work__body linear-dashboard">
        <SpaceContextSummary key={`${session.nodeId}:${spaceId}`} spaceId={spaceId} />
        <section class="linear-dashboard__layout" aria-label="Space 대시보드">
          <section class="linear-dashboard__main" aria-labelledby="linear-dashboard-work-title">
            <header class="linear-dashboard__section-head">
              <h2 id="linear-dashboard-work-title">진행 중인 작업</h2>
            </header>

            <section class="linear-dashboard__group" aria-labelledby="linear-dashboard-conversations">
              <header class="linear-dashboard__group-head">
                <h3 id="linear-dashboard-conversations">대화</h3>
              </header>
              <Resource resource={threads} loadingLabel="대화 목록을 읽는 중" compact>
                {(data) => (data.threads ?? []).length === 0 ? (
                  <Empty
                    title="아직 대화가 없습니다."
                    action={!archived ? <ButtonLink size="sm" href={`/s/${spaceId}/t/new`}>첫 대화 열기</ButtonLink> : null}
                  />
                ) : (
                  <>
                    <List class="linear-dashboard__list">
                      {(data.threads ?? []).map((thread) => (
                        <ListRow key={thread.threadId} href={`/s/${spaceId}/t/${thread.threadId}`}>
                          <span class="linear-dashboard__kind linear-dashboard__kind--thread" aria-hidden="true" />
                          <span class="grow"><span class="t-body-strong">{thread.topic}</span></span>
                          <span class="t-meta">메시지 {thread.lastSequence ?? 0}</span>
                        </ListRow>
                      ))}
                    </List>
                    <ButtonLink class="linear-dashboard__more" size="xs" tone="ghost" href={`/s/${spaceId}/threads`}>
                      대화 전체 보기 <Icon name="arrow" />
                    </ButtonLink>
                  </>
                )}
              </Resource>
            </section>

            <section class="linear-dashboard__group" aria-labelledby="linear-dashboard-tasks">
              <header class="linear-dashboard__group-head">
                <h3 id="linear-dashboard-tasks">Task</h3>
              </header>
              <Resource resource={tasks} loadingLabel="Task를 읽는 중" compact>
                {(data) => (data.tasks ?? []).length === 0 ? (
                  <Empty title="아직 이어지는 Task가 없습니다.">대화에서 필요한 판단만 Task로 이어집니다.</Empty>
                ) : (
                  <>
                    <List class="linear-dashboard__list">
                      {(data.tasks ?? []).map((task) => (
                        <li class="list__row task-reentry-entry" key={task.taskId}>
                          <Link class="list__hit" href={`/s/${spaceId}/task/${task.taskId}`}>
                          <span class="linear-dashboard__kind linear-dashboard__kind--task" aria-hidden="true" />
                          <span class="grow"><span class="t-body-strong">{task.title}</span></span>
                          <span class="linear-dashboard__status t-meta">{task.currentVersionNo ? `공동 이해 v${task.currentVersionNo}` : '검토 중'}</span>
                          </Link>
                          <Link class="task-reentry-link t-meta" href={`/s/${spaceId}/task/${task.taskId}/reentry`} aria-label={`${task.title} · 맥락부터 읽기`}>맥락부터 읽기</Link>
                        </li>
                      ))}
                    </List>
                    <ButtonLink class="linear-dashboard__more" size="xs" tone="ghost" href={`/s/${spaceId}/tasks`}>
                      Task 전체 보기 <Icon name="arrow" />
                    </ButtonLink>
                  </>
                )}
              </Resource>
            </section>
          </section>

        </section>

        <section class="linear-dashboard__context" aria-label="공동 작업 맥락">
            <section class="linear-dashboard__context-card" aria-labelledby="linear-dashboard-inbox">
              <header class="linear-dashboard__group-head">
                <h2 id="linear-dashboard-inbox">수신함</h2>
                <ButtonLink size="xs" tone="ghost" href={`/s/${spaceId}/inbox`}>열기</ButtonLink>
              </header>
              <Resource resource={inbox} loadingLabel="수신함을 읽는 중" compact>
                {(data) => {
                  const count = (data.pointers ?? []).length;
                  return count === 0 ? (
                    <p class="linear-dashboard__empty"><span aria-hidden="true" />다시 읽을 판단이 없습니다.</p>
                  ) : (
                    <p class="linear-dashboard__inbox-count"><strong>{count}</strong><span>다시 읽을 판단</span></p>
                  );
                }}
              </Resource>
            </section>

            <section class="linear-dashboard__context-card" aria-labelledby="linear-dashboard-members">
              <header class="linear-dashboard__group-head">
                <h2 id="linear-dashboard-members">참여자</h2>
                {members.length > 0 ? <span class="t-meta">{members.length}명</span> : null}
              </header>
              {members.length === 0 ? (
                <p class="linear-dashboard__quiet t-meta">참여자를 읽지 못했습니다.</p>
              ) : (
                <div class="linear-dashboard__member-summary">
                  <ul class="linear-dashboard__member-stack" aria-label={`참여자 ${members.length}명`}>
                    {visibleMembers.map((member) => (
                      <li key={member.nodeId} title={member.displayName ?? '이름을 아직 읽지 못했습니다'}>
                        {(member.displayName ?? '?').slice(0, 1)}
                      </li>
                    ))}
                    {members.length > visibleMembers.length ? <li class="linear-dashboard__member-more">+{members.length - visibleMembers.length}</li> : null}
                  </ul>
                  <div class="linear-dashboard__member-copy">
                    <p class="t-body-strong">{leadMember?.displayName ?? '참여자'}</p>
                    <p class="t-meta">{members.length === 1 ? roleLabel(leadMember?.role) : '함께 읽는 중'}</p>
                  </div>
                </div>
              )}
            </section>
        </section>

        <section class="linear-dashboard__admin" aria-label="Space 관리">
          {/* 권한 없음은 오류가 아니라 **부재**다. joinRequests === null 이면 그 칸이 없는 것이다. */}
          {joinRequests === null ? null : (
            <section class="linear-dashboard__admin-item" aria-labelledby="linear-dashboard-requests">
              <header class="linear-dashboard__group-head">
                <h2 id="linear-dashboard-requests">참여 신청</h2>
              </header>
              {joinRequests.length === 0 ? (
                <p class="linear-dashboard__quiet t-meta">대기 중인 신청이 없습니다.</p>
              ) : (
                <div class="promise-list">
                  {joinRequests.map((request) => (
                    <JoinRequestRow
                      key={request.joinRequestId}
                      request={request}
                      spaceId={spaceId}
                      revision={revision}
                      archived={archived}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          <SpaceCode spaceId={space?.spaceId} />
        </section>
      </div>
    </section>
  );
}

function SpaceCode({ spaceId }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!spaceId || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(spaceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section class="linear-dashboard__admin-item" aria-labelledby="space-code-title">
      <header class="linear-dashboard__group-head">
        <h2 id="space-code-title">공간 코드</h2>
      </header>
      <p class="t-meta">다른 사람이 이 Space에 참여할 때 쓰는 코드입니다.</p>
      {spaceId ? (
        <div class="linear-dashboard__code-row">
          <code class="space-code t-mono" title={spaceId}>{spaceId}</code>
          <Button size="xs" tone="ghost" onClick={copy} disabled={!spaceId} aria-label="공간 코드 복사">
            <Icon name="copy" />
          </Button>
        </div>
      ) : <p class="t-meta">지금은 코드를 읽을 수 없습니다.</p>}
    </section>
  );
}

function JoinRequestRow({ request, spaceId, revision, archived }) {
  const session = useSession();
  const [rejecting, setRejecting] = useState(false);
  const approve = useCommand(
    (params, idempotencyKey) => command('join_approve', params, { nodeId: session.nodeId, idempotencyKey }),
    // 승인은 멤버십을 바꾼다 — 참여자 목록·개정까지 이 Space 의 읽기 전부를 다시 읽는다.
    { invalidates: [`space:${spaceId}`] },
  );
  const reject = useCommand(
    (params, idempotencyKey) => command('join_reject', params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}`] },
  );
  const requester = request.requester ?? {};
  const canApprove = Number.isInteger(revision);

  return (
    <article class="promise">
      <div class="row">
        <span class="t-body-strong">{requester.displayName ?? '이름을 아직 읽지 못했습니다'}</span>
        {requester.kind && requester.kind !== 'human' ? (
          <Chip>{requester.kind === 'ai' ? 'AI' : '사람+AI'}</Chip>
        ) : null}
        <span class="t-meta">{requestDate(request.requestedAt)}</span>
      </div>
      {request.message ? <p class="t-body">“{request.message}”</p> : null}
      {archived ? (
        <p class="t-meta">보관된 공간입니다. 신청을 처리할 수 없습니다.</p>
      ) : (
        <>
          <div class="row">
            {canApprove ? (
              <Button
                size="sm"
                busy={approve.pending}
                onClick={() => approve.run({
                  spaceId,
                  joinRequestId: request.joinRequestId,
                  expectedAuthorityRevision: revision,
                })}
              >
                승인
              </Button>
            ) : (
              // 경합 방어값 없이 승인 요청을 지어 보내지 않는다.
              <p class="t-meta">이 배포의 서버가 개정 값을 주지 않아 여기서 승인할 수 없습니다.</p>
            )}
            <Button size="sm" tone="danger" onClick={() => setRejecting((open) => !open)}>
              {rejecting ? '거절 접기' : '거절'}
            </Button>
          </div>
          {rejecting ? (
            <Form
              class="task-form stack"
              busy={reject.pending}
              onSubmit={(event) => {
                const reason = new FormData(event.currentTarget).get('reason')?.trim();
                if (reason) {
                  reject.run({
                    spaceId,
                    joinRequestId: request.joinRequestId,
                    requestVersion: request.requestVersion,
                    reason,
                  });
                }
              }}
            >
              <Field label="거절 근거" htmlFor={`reject-${request.joinRequestId}`} required
                hint="근거는 기록으로 남습니다. 반대 의견도 공동 기억입니다.">
                <Textarea id={`reject-${request.joinRequestId}`} name="reason" />
              </Field>
              <Button kind="submit" tone="danger" size="md" busy={reject.pending}>거절 기록</Button>
            </Form>
          ) : null}
          {approve.error ? <Notice tone="danger" title={describeError(approve.error).message} /> : null}
          {reject.error ? <Notice tone="danger" title={describeError(reject.error).message} /> : null}
        </>
      )}
    </article>
  );
}

function requestDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium' }).format(d);
}

function roleLabel(role) {
  if (role === 'owner') return '오너';
  if (role === 'steward') return '관리';
  return '참여';
}
